import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, ArrowLeft, UserCircle, Search, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AddressAutocomplete from "@/components/ui/AddressAutocomplete";

export default function Addresses() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/addresses");
      setItems(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await api.delete(`/addresses/${id}`);
      toast.success("Address removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const q = search.trim().toLowerCase();
  const filtered = !q
    ? items
    : items.filter((a) =>
        [
          a.label, a.line1, a.line2, a.suburb, a.state, a.postcode,
          a.contact_name, a.contact_phone, a.notes, a.operating_hours,
          a.owner_name, a.owner_business_name,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );

  if (showAdd || editing) {
    return (
      <AddressForm
        initial={editing}
        onClose={() => { setShowAdd(false); setEditing(null); }}
        onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="addresses-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="brand-title text-2xl md:text-3xl">Address Book</h1>
          <p className="text-white/60 text-sm mt-1">
            {isAdmin
              ? "Reusable pickup & drop-off locations — yours and ones added by clients."
              : "Your reusable pickup & drop-off locations."}
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="pill-btn flex items-center gap-2 !text-sm" data-testid="add-address-btn">
          <Plus size={16} /> NEW ADDRESS
        </button>
      </div>

      <div className="relative mb-5 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        <input
          type="text"
          placeholder={isAdmin
            ? "Search label, address, contact or client…"
            : "Search label, address or contact…"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="df-input-rect !py-2 !text-sm !pl-9 w-full"
          data-testid="address-search"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.length === 0 && <p className="text-white/50">No addresses yet.</p>}
        {items.length > 0 && filtered.length === 0 && (
          <p className="text-white/50">No addresses match "{search}".</p>
        )}
        {filtered.map((a) => (
          <div key={a.id} className="rounded-2xl p-5 border border-white/10 bg-white/[0.03]" data-testid={`addr-${a.id}`}>
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#ec489922", color: "#ec4899" }}><MapPin size={16} /></span>
              <div>
                <h3 className="brand-title text-base">{a.label}</h3>
                <p className="text-white/70 text-sm mt-0.5">{a.line1}</p>
                {a.line2 && <p className="text-white/60 text-sm">{a.line2}</p>}
                <p className="text-white/60 text-sm">{[a.suburb, a.state, a.postcode].filter(Boolean).join(" ")}</p>
              </div>
            </div>
            {/* Admin only — flag entries a client added to their own book */}
            {isAdmin && a.owner_role === "client" && (
              <div
                className="mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full"
                style={{ background: "#3b82f622", color: "#93c5fd" }}
              >
                <UserCircle size={13} />
                Added by client · {a.owner_business_name || a.owner_name || "Unknown"}
              </div>
            )}
            {a.operating_hours && (
              <div
                className="mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full"
                style={{ background: "#19e6c822", color: "#19e6c8" }}
              >
                <Clock size={12} /> {a.operating_hours}
              </div>
            )}
            <div className="mt-3 text-xs text-white/60 space-y-0.5">
              {a.contact_name && <p>Contact: <span className="text-white">{a.contact_name}</span></p>}
              {a.contact_phone && <p>Phone: <span className="text-white">{a.contact_phone}</span></p>}
              {a.notes && <p className="italic">"{a.notes}"</p>}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setEditing(a)} className="text-xs px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 flex items-center gap-1" data-testid={`edit-addr-${a.id}`}><Pencil size={12} /> Edit</button>
              <button onClick={() => remove(a.id)} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 flex items-center gap-1" data-testid={`delete-addr-${a.id}`}><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddressForm({ initial, onClose, onSaved }) {
  const [data, setData] = useState({
    label: "", line1: "", line2: "", suburb: "", state: "", postcode: "",
    contact_name: "", contact_phone: "", notes: "", operating_hours: "",
    ...(initial || {}),
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial) await api.put(`/addresses/${initial.id}`, data);
      else await api.post("/addresses", data);
      toast.success("Saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up" data-testid="address-form-page">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors shrink-0"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h3 className="brand-title text-2xl">{initial ? "Edit" : "New"} Address</h3>
      </div>

      <form onSubmit={save} className="bg-[#15151f] border border-white/10 rounded-2xl p-6 sm:p-8 w-full max-w-2xl">
        <div className="grid gap-4">
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Business Name</label>
            <input className="df-input-rect" placeholder="e.g. ACME Logistics" value={data.label} onChange={(e) => setData({ ...data, label: e.target.value })} required />
          </div>
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Address Line 1</label>
            <AddressAutocomplete
              className="df-input-rect"
              placeholder="Start typing to search…"
              value={data.line1}
              onChange={(v) => setData({ ...data, line1: v })}
              onResolved={(p) => setData((d) => ({ ...d, line1: p.line1, suburb: p.suburb, state: p.state, postcode: p.postcode }))}
              required
            />
          </div>
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Address Line 2</label>
            <input className="df-input-rect" placeholder="Unit, suite, floor (optional)" value={data.line2} onChange={(e) => setData({ ...data, line2: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-white/85 mb-2 font-medium text-sm">Suburb</label>
              <input className="df-input-rect" placeholder="Suburb" value={data.suburb} onChange={(e) => setData({ ...data, suburb: e.target.value })} />
            </div>
            <div>
              <label className="block text-white/85 mb-2 font-medium text-sm">State</label>
              <input className="df-input-rect" placeholder="State" value={data.state} onChange={(e) => setData({ ...data, state: e.target.value })} />
            </div>
            <div>
              <label className="block text-white/85 mb-2 font-medium text-sm">Postcode</label>
              <input className="df-input-rect" placeholder="Postcode" value={data.postcode} onChange={(e) => setData({ ...data, postcode: e.target.value })} />
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#19e6c81f", color: "#19e6c8" }}>
                <Clock size={16} />
              </span>
              <div>
                <h3 className="brand-title text-sm">Operating Hours</h3>
                <p className="text-[11px] text-white/45 mt-0.5">When this location can receive pickups/deliveries</p>
              </div>
            </div>
            <input
              className="df-input-rect"
              placeholder="e.g. Mon–Fri 7:00 AM – 4:00 PM"
              value={data.operating_hours}
              onChange={(e) => setData({ ...data, operating_hours: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Contact Name</label>
            <input className="df-input-rect" placeholder="Contact name" value={data.contact_name} onChange={(e) => setData({ ...data, contact_name: e.target.value })} />
          </div>
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Contact Phone</label>
            <input className="df-input-rect" placeholder="Contact phone" value={data.contact_phone} onChange={(e) => setData({ ...data, contact_phone: e.target.value })} />
          </div>
          <div>
            <label className="block text-white/85 mb-2 font-medium text-sm">Notes</label>
            <textarea rows="3" className="df-input-rect resize-none" placeholder="Optional notes" value={data.notes} onChange={(e) => setData({ ...data, notes: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <button type="submit" disabled={busy} className="px-6 py-2.5 rounded-lg text-white font-semibold" style={{ background: "linear-gradient(90deg, #2b5fff, #3b82f6)" }}>
            {busy ? "Saving..." : "Save Address"}
          </button>
        </div>
      </form>
    </div>
  );
}
